export interface CalculateWeekSummaryOptions {
  activeColor: string;
  freeColor: string;
  totalLabel: string;
  freeLabel: string;
  dayLabels: readonly string[];
  dayAccessibilitySuffix: string;
  /** Singular form of `dayAccessibilitySuffix`, used for a day whose count is exactly 1. */
  dayAccessibilitySuffixOne: string;
}

export interface WeekSummaryDay {
  /** Localized binary label; absent for quantified summaries. */
  displayValue?: string;
  value: number;
  backgroundColor: string;
  accessibilityLabel: string;
  isFree: boolean;
}

export interface WeekSummary {
  values: number[];
  total: number;
  freeDays: number;
  totalBadge: { type: 'numeric'; value: string };
  freeBadge: { type: 'numeric'; value: string };
  totalAccessibilityLabel: string;
  freeAccessibilityLabel: string;
  days: WeekSummaryDay[];
}

export interface WeekTotals {
  /** The raw week normalised to 7 clamped non-negative integers. */
  values: number[];
  total: number;
  freeDays: number;
}

/**
 * The numeric core of a logged week, split out from `calculateWeekSummary` so a
 * caller that needs only the arithmetic (e.g. recap copy) shares one definition
 * of how a raw answer becomes a drink count — including the upper clamp — rather
 * than re-deriving it and drifting.
 */
export function weekTotals(rawValues: readonly unknown[]): WeekTotals {
  const values = rawValues.map(normaliseDailyValue);
  return {
    values,
    total: values.reduce((sum, value) => sum + value, 0),
    freeDays: values.filter((value) => value === 0).length,
  };
}

export function calculateWeekSummary(
  rawValues: readonly unknown[],
  options: CalculateWeekSummaryOptions,
): WeekSummary {
  const { values, total, freeDays } = weekTotals(rawValues);

  return {
    values,
    total,
    freeDays,
    totalBadge: { type: 'numeric', value: String(total) },
    freeBadge: { type: 'numeric', value: String(freeDays) },
    totalAccessibilityLabel: `${options.totalLabel}: ${total} per week`,
    freeAccessibilityLabel: `${options.freeLabel}: ${freeDays} ${freeDays === 1 ? 'day' : 'days'} per week`,
    days: values.map((value, index) => {
      const isFree = value === 0;
      const dayLabel = options.dayLabels[index] ?? `Day ${index + 1}`;
      const suffix =
        value === 1 ? options.dayAccessibilitySuffixOne : options.dayAccessibilitySuffix;
      return {
        value,
        backgroundColor: isFree ? options.freeColor : options.activeColor,
        accessibilityLabel: `${dayLabel}: ${value} ${suffix}`,
        isFree,
      };
    }),
  };
}

function normaliseDailyValue(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.min(30, Math.max(0, Math.trunc(value)));
}
